import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, interval, takeUntil } from 'rxjs';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import {
    DidDataRetrievalService,
    t_DataIdentifier,
    t_DataPoint,
} from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { dataIdentifierEqual } from 'src/app/services/helper/did-handling-helper';
import { t_EquipmentCurrentsDataset, initEquipmentCurrentDataset, e_Positions } from '../data/equipmentCurrents-dataNames';
import { BasicMachineFunctionsService } from 'src/app/services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-equipment-currents',
    templateUrl: './equipment-currents.component.html',
    styleUrls: ['./equipment-currents.component.scss'],
})
export class EquipmentCurrentsComponent implements OnInit, OnDestroy {
    public equipmentCurrentsDataset: t_EquipmentCurrentsDataset = JSON.parse(JSON.stringify(initEquipmentCurrentDataset));
    public queryFrequency: number = 1000;
    public loadedAndQueryStarting: boolean = false;
    public imgLoaded: boolean = false;
    public pollingFrequency: number;
    private destroy$: Subject<void> = new Subject<void>();
    public positionEnum = e_Positions;
    private destroyed: boolean = false;

    constructor(
        public gatewayConnectionService: GatewayConnectionService,
        private didConfigService: DidConfigService,
        private dataRetrieval: DidDataRetrievalService,
        private apiConnection: BasicMachineFunctionsService,
        public router: Router
    ) {
        if (this.queryFrequency <= 65) {
            this.pollingFrequency = 50;
        } else {
            this.pollingFrequency = parseFloat((this.queryFrequency * 0.75).toFixed(0));
        }
    }

    ngOnInit(): void {
        this.setupServices().then(() => {
            if(!this.destroyed) {
                this.dataRetrieval.initService();
                this.getDataIdentifiersToDataNamesFromConfig();
                this.postDataIdentifierToQueryToBackend().then(() => {
                    if(this.destroyed){
                        this.ngOnDestroy();
                    }
                    this.dataRetrieval.startDataQuery();
                    this.loadedAndQueryStarting = true;
                    this.startPeriodicPolling();
            });
            // Init Data Query List
            // postDataQueryListToBackend
            }
        });
    }
    ngOnDestroy(): void {
        this.destroyed = true;
        this.destroy$.next();
        this.destroy$.complete();
        this.dataRetrieval.stopSendQueryAliveState();
        this.dataRetrieval.stopDataQuery();
        this.dataRetrieval.cleanDataPoints();
        this.gatewayConnectionService.checkGatewayAlive().subscribe({
            next: (isAlive) => {
                if(isAlive){
                    this.equipmentCurrentsDataset.dataPoints.forEach(data => {
                        this.didConfigService.removeDidFromQueryList({ecu_id: data.dataName.identifier!.ecu_id, data_id: data.dataName.identifier!.data_id}).subscribe();
                    });
                }
            }
        });
    }

    private setupServices(): Promise<void> {
        return new Promise((resolve) => {
            this.gatewayConnectionService.establishConnectionToGateway().then(
                () => {
                    resolve();
                    // Resolve, Connection established, Load DidMapping
                    /*this.didConfigService.loadEcuList().then(() => {
                        this.didConfigService.ecuList!.forEach((ecu) => {
                            this.didConfigService.setEcuDidMapping(ecu.ecu_id).then(() => {
                                resolve();
                            });
                        });
                    });*/
                },
                () => {
                    // Rejected, no connection possible
                    console.error('ERROR');
                }
            );
        });
    }

    public getDataIdentifiersToDataNamesFromConfig() {
        this.equipmentCurrentsDataset.dataPoints.forEach((equipmentCurrentsPoint) => {
            equipmentCurrentsPoint.dataName.identifier = this.didConfigService.getDataIdentifierOfDataName(equipmentCurrentsPoint.dataName.name);
            equipmentCurrentsPoint.dataName.didInformation = this.didConfigService.getDidInformation(
                equipmentCurrentsPoint.dataName.identifier
            );
        });
        this.equipmentCurrentsDataset.dataPoints = this.equipmentCurrentsDataset.dataPoints
            .map((dataPoint) => dataPoint)
            .filter((dataPoint) => dataPoint.dataName.identifier?.ecu_id !== undefined);
        if(this.destroyed) {
            this.ngOnDestroy();
        }
    }

    private postDataIdentifierToQueryToBackend(): Promise<void> {
        return new Promise((resolve) => {
            let dataIdentifierList: t_DataIdentifier[] = this.equipmentCurrentsDataset.dataPoints
                .map((dataPoint) => dataPoint.dataName.identifier)
                .filter((identifier) => identifier?.ecu_id !== undefined) as t_DataIdentifier[];
            this.didConfigService.postNewDidQueryList(dataIdentifierList).subscribe({
                complete: () => {
                    resolve();
                },
            });
        });
    }

    private startPeriodicPolling() {
        interval(this.pollingFrequency)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.getDataFromBackend();
            });
    }

    private getDataFromBackend() {
        // Get all Dids Values, because did list was Posted, so no other dids are present in the list
        this.dataRetrieval.getAllDidsValues().subscribe((response) => {
            if (response.body != null) {
                this.processRawData(response.body);
            }
        });
    }

    private processRawData(rawData: t_DataPoint[]) {
        rawData.forEach((dataPoint) => {
            if (!dataPoint.data || new Date(dataPoint.data.timestamp).getTime() < 0) {
                return;
            }
            let indexInDataset: number = this.findIndexForIdentifierDataset(dataPoint.identifier);
            let normalizedValue: number = this.normalizedValue(
                dataPoint.data.value[0],
                this.equipmentCurrentsDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
                this.equipmentCurrentsDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
            );
            this.equipmentCurrentsDataset.dataPoints[indexInDataset].value = normalizedValue;
            this.equipmentCurrentsDataset.dataPoints[indexInDataset].unit = this.equipmentCurrentsDataset.dataPoints[indexInDataset].dataName.didInformation!.unit;
        });
    }

    private findIndexForIdentifierDataset(searchIdentifier: t_DataIdentifier): number {
        let index: number = this.equipmentCurrentsDataset.dataPoints.findIndex((datapoint) => {
            if (datapoint.dataName.identifier) {
                return dataIdentifierEqual(datapoint.dataName.identifier, searchIdentifier);
            } else {
                return false;
            }
        });
        return index;
    }

    private normalizedValue(value: number, factor: number, offset: number) {
        if(factor == 0) {
            factor = 1;
        }
        let res: number = (value * factor) + offset;
        res = parseFloat(res.toFixed(1));
        return res;
    }
}
